import { queryOptions, useQuery } from "@tanstack/react-query";
import { auth, isAal2, mfaStepOf, tenantIdOf } from "./auth";
import { isDemoMode, supabase } from "./supabase";

// Shape returned by public.get_session_context() — the app shell's single
// start-up round-trip (user, active tenant, tenants, companies, permissions).
export interface SessionContext {
  user: { id: string; email: string; full_name: string | null; locale: "pl" | "en" };
  tenant: { id: string; name: string; slug: string } | null;
  tenants: { id: string; name: string }[];
  companies: { id: string; code: string; name: string; currency: string }[];
  permissions: string[];
}

const demoContext: SessionContext = {
  user: { id: "demo", email: "demo@patronix.local", full_name: "Demo User", locale: "en" },
  tenant: { id: "demo", name: "Demo Manufacturing", slug: "demo" },
  tenants: [{ id: "demo", name: "Demo Manufacturing" }],
  companies: [{ id: "demo-pl01", code: "PL01", name: "Zakład Łódź", currency: "PLN" }],
  permissions: [],
};

export const sessionContextQuery = queryOptions({
  queryKey: ["session", "context"] as const,
  queryFn: async (): Promise<SessionContext> => {
    if (isDemoMode || !supabase) return demoContext;
    const { data, error } = await supabase.rpc("get_session_context");
    if (error) throw error;
    const ctx = data as SessionContext;
    // The token names a tenant but the database closed it: the organization
    // tightened its two-step sign-in policy since this token was issued
    // (docs/design/security.md). A fresh token carries the new requirement;
    // then the code screen takes over.
    const session = auth.get();
    if (!ctx.tenant && tenantIdOf(session) && !isAal2(session)) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (mfaStepOf(refreshed.session)) {
        window.location.assign(`/mfa?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      }
    }
    return ctx;
  },
  // Changes only on tenant switch / role changes; both clear the cache.
  staleTime: 5 * 60_000,
});

export function useSessionContext() {
  return useQuery(sessionContextQuery);
}

/** Permission check for UI affordances. The database enforces the real rule. */
export function useCan(permission: string): boolean {
  const { data } = useSessionContext();
  if (isDemoMode) return true;
  return data?.permissions.includes(permission) ?? false;
}

export function initialsOf(name: string | null | undefined, email: string): string {
  const source = name?.trim() || email.split("@")[0] || "?";
  const parts = source.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? (parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "") : source.slice(0, 2);
  return letters.toLocaleUpperCase();
}

export function firstNameOf(name: string | null | undefined, email: string): string {
  return name?.trim().split(/\s+/)[0] || email.split("@")[0] || "";
}
